import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType } from 'keycloak-angular';
import { MessageService } from '@openng/optimus-ui/api';
import { Optimus } from '@openng/optimus-ui/config';
import { AuthenticationService, AuthorizationService } from '~ngx-shared/authentication';
import { GraphQlService } from '~ngx-shared/graph-ql';
import { LayoutConfig, LayoutService, LoadingService, MenuService } from '~ngx-shared/layout';
import { FileService, VersionService } from '~ngx-shared/services';
import { environment } from '~shared/environment';
import { UserService } from '~shared/services';
import { AuthorizationUtil, RoleEnum } from '~shared/utils';
import { PendingService } from './services/pending.service';
import { Membership } from './theme/membership';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly layoutService = inject(LayoutService);
  private readonly translocoService = inject(TranslocoService);
  private readonly loadingService = inject(LoadingService);
  private readonly menuService = inject(MenuService);
  private readonly versionService = inject(VersionService);
  private readonly messageService = inject(MessageService);
  private readonly optimusConfig = inject(Optimus);
  private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly graphQlService = inject(GraphQlService);
  private readonly pendingService = inject(PendingService);

  constructor() {
    const authenticationService = inject(AuthenticationService);
    const userService = inject(UserService);
    const fileService = inject(FileService);

    fileService.init(
      `${environment.externalServiceUrl}/file/upload`,
      `${environment.externalServiceUrl}/file`
    );

    effect(() => {
      const isNewAvailable = this.versionService.isNewVersionAvailable();
      if (isNewAvailable) {
        this.messageService.add({
          severity: 'error',
          summary: 'update',
          closable: false,
          sticky: true,
          key: 'new_version_available'
        });
      }
    });

    effect(() => {
      const keycloakEvent = this.keycloakSignal();
      if (keycloakEvent.type === KeycloakEventType.Ready) {
        authenticationService.init(
          ['default-roles-iggoe', 'iggoe-user'],
          url =>
            url.startsWith(environment.hasuraUrl) || url.startsWith(environment.externalServiceUrl)
        );
        this.authorizationService.setAllowedRoles(
          Object.values(AuthorizationUtil.GROUP_MEMBERSHIP_ALL)
        );
        userService.init();

        this.menuService.sideBarItems$.next([
          {
            label: 'Home',
            items: [
              {
                label: 'dashboard',
                icon: 'pi pi-fw pi-home',
                routerLink: ['/', 'dashboard']
              },
              {
                label: 'profile',
                icon: 'pi pi-fw pi-user',
                routerLink: ['/', 'profile']
              }
            ]
          },
          {
            label: 'shared.actors',
            items: [
              {
                label: 'membership.title',
                icon: 'pi pi-fw pi-inbox',
                routerLink: ['/', 'actors', 'membership-applications']
              },
              {
                label: 'membership.military_title',
                icon: 'pi pi-fw pi-inbox',
                routerLink: ['/', 'actors', 'military-applications']
              },
              {
                label: 'membership.conversion_title',
                icon: 'pi pi-fw pi-inbox',
                routerLink: ['/', 'actors', 'conversion-applications']
              },
              {
                label: 'shared.school_enrolment_title',
                icon: 'pi pi-fw pi-inbox',
                routerLink: ['/', 'actors', 'school-enrolment-applications']
              },
              {
                label: 'shared.role.members',
                icon: 'pi pi-fw pi-briefcase',
                routerLink: ['/', 'actors', 'members']
              },
              {
                label: 'shared.role.membership_offices',
                icon: 'pi pi-fw pi-briefcase',
                routerLink: ['/', 'actors', 'member-offices'],
                state: { permissions: RoleEnum.MODERATOR }
              },
              {
                label: 'shared.people',
                icon: 'pi pi-fw pi-users',
                routerLink: ['/', 'actors', 'people']
              }
            ]
          },
          {
            label: 'shared.administration',
            items: [
              {
                label: 'shared.actors',
                icon: 'pi pi-fw pi-users',
                routerLink: ['/', 'administration', 'actors']
              },
              {
                label: 'shared.mail_templates',
                icon: 'pi pi-fw pi-envelope',
                routerLink: ['/', 'mail-templates'],
                state: { permissions: AuthorizationUtil.GROUP_MEMBERSHIP_ALL }
              },
              {
                label: 'shared.print_templates',
                icon: 'pi pi-fw pi-file-pdf',
                routerLink: ['/', 'print-templates'],
                state: { permissions: AuthorizationUtil.GROUP_MEMBERSHIP_ALL }
              }
            ]
          }
        ]);
      }
    });
  }

  ngOnInit(): void {
    this.loadingService.startLoading();

    //optional configuration with the default configuration
    const config: LayoutConfig = {
      preset: 'Aura',
      primary: 'emerald',
      surface: null,
      darkTheme: false,
      menuMode: 'static'
    };
    this.layoutService.layoutConfig.set(config);
    this.optimusConfig.theme.set({
      ...this.optimusConfig.theme(),
      preset: Membership
    });

    this.translocoService.selectTranslateObject('primeng').subscribe(translated => {
      this.optimusConfig.setTranslation(translated);
    });

    this.authorizationService.currentRole$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(role => {
        if (role) {
          this.graphQlService.init(() => role);
        }
      });

    this.pendingService.init();

    this.loadingService.stopLoading();
  }
}
